import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZCalendar — « Réservations payées ».
// Liste les créneaux réellement encaissés (Mobile Money confirmé) et permet
// de suivre les no-shows (client qui ne vient pas au rendez-vous).
// Aucune donnée n'est inventée : tout vient de booking_appointments.
// ─────────────────────────────────────────────────────────────

interface ServiceRow {
  id: number;
  service_name: string | null;
  currency: string | null;
}

interface AppointmentRow {
  id: number;
  service_id: number;
  fullname: string | null;
  email: string | null;
  phone: string | null;
  date: string;
  time: string;
  status: string | null;
  amount: number | null;
  currency: string | null;
  payment_status: string | null;
  paid_at: string | null;
}

interface Props {
  ownerId: number;
  defaultCurrency: string;
  onToast?: (message: string) => void;
}

const STATUS_META: Record<string, { label: string; className: string; icon: string }> = {
  paid: { label: 'Payé', className: 'bg-accent-100 text-accent-900', icon: 'ri-check-double-line' },
  no_show: { label: 'No-show', className: 'bg-red-100 text-red-800', icon: 'ri-user-unfollow-line' },
  completed: { label: 'Honoré', className: 'bg-secondary-100 text-secondary-900', icon: 'ri-calendar-check-line' },
  cancelled: { label: 'Annulé', className: 'bg-background-200/70 text-foreground-600', icon: 'ri-close-circle-line' },
  confirmed: { label: 'Confirmé', className: 'bg-accent-50 text-accent-800', icon: 'ri-calendar-check-line' },
};

function fmtDate(value: string): string {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function money(value: number, currency: string): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency || ''}`.trim();
}

export default function PaidBookingsTab({ ownerId, defaultCurrency, onToast }: Props) {
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | 'paid' | 'no_show'>('all');

  const load = useCallback(async () => {
    if (!ownerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: svcData, error: svcError } = await supabase
        .from('booking_services')
        .select('id, service_name, currency')
        .eq('owner', ownerId);
      if (svcError) throw svcError;

      const rows = (svcData || []) as ServiceRow[];
      setServices(rows);

      const ids = rows.map((s) => Number(s.id)).filter((n) => n > 0);
      if (ids.length === 0) {
        setAppointments([]);
        return;
      }

      const { data: apptData, error: apptError } = await supabase
        .from('booking_appointments')
        .select('id, service_id, fullname, email, phone, date, time, status, amount, currency, payment_status, paid_at')
        .in('service_id', ids)
        .order('date', { ascending: false });
      if (apptError) throw apptError;

      // On garde les créneaux encaissés + les no-shows.
      const relevant = ((apptData || []) as AppointmentRow[]).filter(
        (a) => String(a.payment_status || '').toLowerCase() === 'paid'
          || String(a.status || '').toLowerCase() === 'no_show'
          || (Number(a.amount) || 0) > 0,
      );
      setAppointments(relevant);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les réservations payées.');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  const serviceName = useMemo(() => {
    const map = new Map<number, string>();
    services.forEach((s) => map.set(Number(s.id), s.service_name || `Service #${s.id}`));
    return map;
  }, [services]);

  const todayStr = new Date().toISOString().split('T')[0];

  const stats = useMemo(() => {
    let collected = 0;
    let paidCount = 0;
    let noShowCount = 0;
    let upcoming = 0;
    let lostAmount = 0;
    const currency = appointments[0]?.currency || defaultCurrency;

    appointments.forEach((a) => {
      const isPaid = String(a.payment_status || '').toLowerCase() === 'paid';
      const isNoShow = String(a.status || '').toLowerCase() === 'no_show';
      const amount = Number(a.amount) || 0;
      if (isPaid) {
        collected += amount;
        paidCount += 1;
        if (isNoShow) {
          noShowCount += 1;
          lostAmount += amount;
        } else if (a.date >= todayStr) {
          upcoming += 1;
        }
      } else if (isNoShow) {
        noShowCount += 1;
      }
    });

    return { collected, paidCount, noShowCount, upcoming, lostAmount, currency };
  }, [appointments, defaultCurrency, todayStr]);

  const filtered = appointments.filter((a) => {
    if (filter === 'paid') return String(a.payment_status || '').toLowerCase() === 'paid';
    if (filter === 'no_show') return String(a.status || '').toLowerCase() === 'no_show';
    return true;
  });

  const setStatus = async (appointment: AppointmentRow, status: string, message: string) => {
    setBusyId(appointment.id);
    try {
      const { error: updateError } = await supabase
        .from('booking_appointments')
        .update({ status })
        .eq('id', appointment.id);
      if (updateError) throw updateError;
      setAppointments((prev) => prev.map((a) => (a.id === appointment.id ? { ...a, status } : a)));
      onToast?.(message);
    } catch (e) {
      onToast?.(e instanceof Error ? e.message : 'La mise à jour a échoué.');
    } finally {
      setBusyId(null);
    }
  };

  if (!ownerId) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-10 text-center">
        <p className="text-sm text-foreground-500">Connectez-vous pour consulter vos réservations payées.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Réservations payées</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Les créneaux encaissés en Mobile Money, avec le suivi des clients qui ne se présentent pas.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-accent-200/70 bg-accent-50 p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent-800">
            <i className="ri-hand-coin-line"></i>Encaissé
          </div>
          <p className="mt-1 text-lg font-bold font-heading text-foreground-950 leading-tight">
            {money(stats.collected, stats.currency)}
          </p>
        </div>
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
            <i className="ri-calendar-check-line"></i>Créneaux payés
          </div>
          <p className="mt-1 text-lg font-bold font-heading text-foreground-950">{stats.paidCount}</p>
        </div>
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
            <i className="ri-time-line"></i>À venir payés
          </div>
          <p className="mt-1 text-lg font-bold font-heading text-foreground-950">{stats.upcoming}</p>
        </div>
        <div className="rounded-lg border border-red-200/70 bg-red-50 p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-700">
            <i className="ri-user-unfollow-line"></i>No-shows
          </div>
          <p className="mt-1 text-lg font-bold font-heading text-foreground-950">
            {stats.noShowCount}
            {stats.lostAmount > 0 && (
              <span className="ml-1.5 text-[11px] font-medium text-red-700">
                ({money(stats.lostAmount, stats.currency)} encaissés)
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {([
          { key: 'all' as const, label: 'Tout', icon: 'ri-list-check' },
          { key: 'paid' as const, label: 'Payées', icon: 'ri-secure-payment-line' },
          { key: 'no_show' as const, label: 'No-shows', icon: 'ri-user-unfollow-line' },
        ]).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
              filter === item.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
            }`}
          >
            <i className={item.icon}></i>
            {item.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Rafraîchir
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => void load()} className="underline font-medium whitespace-nowrap cursor-pointer">
            Réessayer
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <p className="mt-3 text-xs text-foreground-400">Chargement des réservations…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-secure-payment-line text-2xl text-foreground-300"></i>
          </div>
          <h3 className="text-sm font-bold text-foreground-800">Aucune réservation payée</h3>
          <p className="mt-1 text-xs text-foreground-500 max-w-md mx-auto">
            Dès qu'un client règle un créneau en Mobile Money, il apparaît ici automatiquement.
            Pensez à renseigner un prix sur vos services (onglet Services).
          </p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="hidden lg:grid grid-cols-[1.1fr_1.4fr_1.2fr_0.9fr_1.3fr] gap-3 px-5 py-2.5 bg-background-100/70 border-b border-background-200/60 text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
            <span>Créneau</span>
            <span>Client</span>
            <span>Service</span>
            <span>Montant</span>
            <span className="text-right">Suivi</span>
          </div>
          <div className="divide-y divide-background-200/50">
            {filtered.map((a) => {
              const isPaid = String(a.payment_status || '').toLowerCase() === 'paid';
              const key = String(a.status || '').toLowerCase();
              const meta = isPaid && key !== 'no_show' && key !== 'completed' && key !== 'cancelled'
                ? STATUS_META.paid
                : STATUS_META[key] || (isPaid ? STATUS_META.paid : STATUS_META.confirmed);
              const amount = Number(a.amount) || 0;
              const currency = a.currency || defaultCurrency;
              const isPast = a.date < todayStr;

              return (
                <div key={a.id} className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr_1.2fr_0.9fr_1.3fr] gap-3 px-5 py-3.5 items-center">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{a.time?.slice(0, 5)}</p>
                    <p className="text-[11px] text-foreground-500">{fmtDate(a.date)}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{a.fullname || 'Client'}</p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {[a.phone, a.email].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-700 truncate">{serviceName.get(Number(a.service_id)) || '—'}</p>
                  </div>
                  <div>
                    {amount > 0 ? (
                      <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{money(amount, currency)}</span>
                    ) : (
                      <span className="text-xs text-foreground-400">Gratuit</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-start lg:justify-end gap-1.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>
                      <i className={meta.icon}></i>
                      {meta.label}
                    </span>
                    {isPaid && isPast && key !== 'no_show' && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void setStatus(a, 'no_show', 'Marqué comme no-show.')}
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-100 text-foreground-600 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        title="Le client ne s'est pas présenté"
                      >
                        No-show
                      </button>
                    )}
                    {isPaid && key === 'no_show' && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void setStatus(a, 'confirmed', 'No-show annulé : le rendez-vous est de nouveau honoré.')}
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        Annuler
                      </button>
                    )}
                    {isPaid && !isPast && key !== 'completed' && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void setStatus(a, 'completed', 'Rendez-vous marqué comme honoré.')}
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        Honoré
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}